import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPrice, titleize, primaryImage, resolveImageUrl } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MapPin, Users, BadgeCheck, Sparkles, CheckCircle2 } from "lucide-react";
import EnquiryForm from "@/components/EnquiryForm";
import VenueCard from "@/components/VenueCard";
const VenueMap = React.lazy(() => import("@/components/VenueMap"));
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator
} from "@/components/ui/breadcrumb";

const DEFAULT_FAQ = [
    { q: "How do I book this venue?", a: "Submit an enquiry via the form on this page. The venue will reply directly with availability, pricing and next steps." },
    { q: "Is Venuely free to use?", a: "Yes — enquiries and browsing are 100% free. You pay only the venue directly for your booking." },
    { q: "Can I hire this venue for a private event?", a: "Yes, this listing offers private hire. Message the venue with your event date, guest count and requirements." },
];

export default function VenueDetail() {
    const { country_slug, city_slug, venue_slug } = useParams();
    const [venue, setVenue] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/venues/by-slug/${country_slug}/${city_slug}/${venue_slug}`)
            .then((r) => setVenue(r.data))
            .catch((e) => setErr(e.response?.status === 404 ? "not_found" : "error"))
            .finally(() => setLoading(false));
    }, [country_slug, city_slug, venue_slug]);

    const schema = venue ? [
        {
            "@context": "https://schema.org",
            "@type": "EventVenue",
            name: venue.name,
            description: venue.short_description,
            image: (venue.images || []).map((i) => i.url).slice(0, 6),
            url: window.location.href,
            address: {
                "@type": "PostalAddress",
                streetAddress: venue.address || undefined,
                addressLocality: venue.city,
                addressRegion: venue.area || undefined,
                addressCountry: venue.country,
            },
            telephone: venue.contact_phone || undefined,
            priceRange: venue.price_from ? `${formatPrice(venue.price_from, venue.price_currency)}+` : undefined,
            maximumAttendeeCapacity: venue.capacity_standing || venue.capacity_seated || undefined,
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
                { "@type": "ListItem", position: 2, name: titleize(country_slug), item: `${window.location.origin}/venues/${country_slug}` },
                { "@type": "ListItem", position: 3, name: titleize(city_slug), item: `${window.location.origin}/venues/${country_slug}/${city_slug}` },
                { "@type": "ListItem", position: 4, name: venue.name, item: window.location.href },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: (venue.faq?.length ? venue.faq : DEFAULT_FAQ).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
        },
    ] : null;

    useSEO({
        title: venue ? `${venue.name}, ${venue.city} — private hire | Venuely` : "Venue",
        description: venue ? `${venue.short_description} Capacity up to ${venue.capacity_standing || venue.capacity_seated || "many"}. Enquire on Venuely.` : "",
        image: venue ? primaryImage(venue) : undefined,
        schema,
    });

    if (loading) return <div className="container-app py-20"><div className="h-8 w-1/3 bg-muted/50 rounded animate-pulse mb-6" /><div className="h-96 bg-muted/40 rounded-xl animate-pulse" /></div>;
    if (err === "not_found") return <div className="container-app py-24 text-center"><h1 className="font-display text-4xl">Venue not found</h1><p className="mt-3 text-muted-foreground">The venue you're looking for may have been moved or archived.</p><Link to="/search" className="mt-6 inline-block text-primary font-medium">Browse venues →</Link></div>;
    if (err) return <div className="container-app py-24 text-center"><p>Something went wrong. Please try again.</p></div>;
    if (!venue) return null;

    const images = venue.images && venue.images.length > 0 ? venue.images : [{ url: primaryImage(venue) }];
    const cap = venue.capacity_standing || venue.capacity_seated;

    return (
        <div className="container-app py-6 md:py-8" data-testid="venue-detail-page">
            <Breadcrumb className="mb-4">
                <BreadcrumbList>
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/venues/${country_slug}/${city_slug}`}>Venues in {titleize(city_slug)}</Link></BreadcrumbLink></BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem><BreadcrumbPage>{venue.name}</BreadcrumbPage></BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            {/* Gallery bento */}
            <div className="grid grid-cols-4 grid-rows-2 gap-2 md:gap-3 rounded-2xl overflow-hidden" data-testid="venue-gallery" style={{ maxHeight: 560 }}>
                <img src={resolveImageUrl(images[0].url)} alt={venue.name} className="col-span-4 md:col-span-2 row-span-2 object-cover w-full h-full min-h-[280px]" />
                {images.slice(1, 5).map((img, i) => (
                    <img key={i} src={resolveImageUrl(img.url)} alt={`${venue.name} ${i + 2}`} className="hidden md:block col-span-1 row-span-1 object-cover w-full h-full" />
                ))}
            </div>

            <div className="grid lg:grid-cols-[1fr_400px] gap-10 mt-8">
                <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        {venue.is_featured && <Badge className="bg-secondary text-secondary-foreground gap-1"><Sparkles className="h-3 w-3" /> Featured</Badge>}
                        {venue.is_verified && <Badge variant="outline" className="border-primary/30 text-primary gap-1"><BadgeCheck className="h-3 w-3" /> Verified</Badge>}
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">{titleize(venue.venue_type)}</span>
                    </div>
                    <h1 className="font-display text-3xl md:text-5xl font-semibold leading-tight" data-testid="venue-title">{venue.name}</h1>
                    <div className="flex items-center gap-2 mt-3 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{venue.area ? `${venue.area}, ` : ""}{venue.city}, {venue.country}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-6 mt-6 pb-6 border-b border-border">
                        {cap ? (
                            <div>
                                <span className="text-xs uppercase tracking-wider text-muted-foreground">Capacity</span>
                                <p className="font-display text-xl font-semibold flex items-center gap-2"><Users className="h-5 w-5 text-secondary" /> Up to {cap}</p>
                            </div>
                        ) : null}
                        <div>
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">From</span>
                            <p className="font-display text-xl font-semibold">{formatPrice(venue.price_from, venue.price_currency)}</p>
                        </div>
                        {venue.pricing_model && (
                            <div>
                                <span className="text-xs uppercase tracking-wider text-muted-foreground">Pricing</span>
                                <p className="font-display text-xl font-semibold">{titleize(venue.pricing_model.replace(/_/g, "-"))}</p>
                            </div>
                        )}
                    </div>

                    <div className="mt-8">
                        <h2 className="font-display text-2xl font-semibold mb-3">About this venue</h2>
                        <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{venue.long_description || venue.short_description}</p>
                    </div>

                    {venue.amenities?.length > 0 && (
                        <div className="mt-8">
                            <h2 className="font-display text-2xl font-semibold mb-3">Amenities</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                                {venue.amenities.map((a) => (
                                    <div key={a} className="flex items-center gap-2 text-sm">
                                        <CheckCircle2 className="h-4 w-4 text-primary" /> {a}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {venue.event_types?.length > 0 && (
                        <div className="mt-8">
                            <h2 className="font-display text-2xl font-semibold mb-3">Perfect for</h2>
                            <div className="flex flex-wrap gap-2">
                                {venue.event_types.map((e) => (
                                    <Link key={e} to={`/events/${e}/${city_slug}`} className="rounded-full bg-muted px-3 py-1.5 text-sm hover:bg-accent transition-colors">
                                        {titleize(e)}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mt-8">
                        <h2 className="font-display text-2xl font-semibold mb-3">Location</h2>
                        <React.Suspense fallback={<div className="rounded-xl border border-border bg-muted/30 h-72 md:h-80 animate-pulse" />}>
                            <VenueMap venue={venue} />
                        </React.Suspense>
                        {(venue.address || venue.area) && (
                            <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {[venue.address, venue.area, venue.city, venue.country].filter(Boolean).join(", ")}
                            </p>
                        )}
                    </div>

                    <div className="mt-10">
                        <h2 className="font-display text-2xl font-semibold mb-3">Frequently asked</h2>
                        <Accordion type="single" collapsible className="border border-border rounded-xl divide-y divide-border">
                            {(venue.faq?.length ? venue.faq : DEFAULT_FAQ).map((f, i) => (
                                <AccordionItem key={i} value={`f${i}`} className="px-4 border-0">
                                    <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>

                <aside className="lg:sticky lg:top-24 self-start">
                    <div className="rounded-2xl border border-border bg-card p-5 md:p-6 shadow-sm">
                        <h3 className="font-display text-xl font-semibold">Enquire about {venue.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">Free and non-binding. The venue will respond directly.</p>
                        <div className="mt-4">
                            <EnquiryForm venueId={venue.venue_id} venueName={venue.name} />
                        </div>
                    </div>
                </aside>
            </div>

            {venue.similar?.length > 0 && (
                <div className="mt-16">
                    <h2 className="font-display text-2xl md:text-3xl font-semibold mb-6">Similar venues nearby</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {venue.similar.map((v, i) => <VenueCard key={v.venue_id} venue={v} index={i} />)}
                    </div>
                </div>
            )}

            {/* Sticky mobile enquire button */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur border-t border-border p-3">
                <a href="#enquire" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="enquiry-form"]')?.scrollIntoView({ behavior: "smooth" }); }} className="block w-full rounded-full bg-primary text-primary-foreground text-center py-3 font-semibold" data-testid="mobile-enquire-cta">
                    Enquire — from {formatPrice(venue.price_from, venue.price_currency)}
                </a>
            </div>
        </div>
    );
}
